import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorCode, apiErrorMessage } from '@/api/client'
import { payroll as payrollApi } from '@/api/endpoints'
import { Alert, Modal, Spinner, Textarea } from '@/components/ui'
import { cn, formatDate, formatMoney, minutesToHours } from '@/lib/utils'
import type { Payslip } from '@/types/api'

/**
 * Výplatné listy inštruktora (`sql/039`) - zoznam s akciami Vyplatené /
 * Zrušiť a odkazom na PDF. Odmeny na liste sa už nemenia; zrušenie ich
 * uvoľní (aj vyplatený list - odmeny sa vrátia na nevyplatené).
 */
export function PayslipList({ instructorId, readOnly }: { instructorId: number; readOnly?: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const { data } = useQuery({
    queryKey: ['payslips', instructorId],
    queryFn: () => payrollApi.payslips(instructorId),
  })
  const refresh = () => {
    setError(null)
    for (const key of ['payslips', 'instructor-rewards', 'rewards-overview']) {
      void qc.invalidateQueries({ queryKey: [key] })
    }
  }
  const paid = useMutation({ mutationFn: payrollApi.markPaid, onSuccess: refresh, onError: (e) => setError(apiErrorMessage(e)) })
  const cancel = useMutation({ mutationFn: payrollApi.cancel, onSuccess: refresh, onError: (e) => setError(apiErrorMessage(e)) })

  if (!data?.length) return null
  return (
    <div className="card overflow-x-auto">
      <h2 className="px-3 pt-3 text-xs font-semibold uppercase text-slate-500">{t('payroll.payslips')}</h2>
      {error ? <div className="px-3 pt-2"><Alert variant="error">{error}</Alert></div> : null}
      <table className="table-base">
        <thead>
          <tr>
            <th>{t('payroll.number')}</th>
            <th>{t('payroll.period')}</th>
            <th className="text-right">{t('statistics.totalHours')}</th>
            <th className="text-right">{t('global.total')}</th>
            <th>{t('payroll.status')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.map((payslip) => (
            <tr key={payslip.id} className={payslip.status === 'cancelled' ? 'text-slate-400' : ''}>
              <td>
                <a
                  href={payrollApi.pdfUrl(payslip.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'inline-flex items-center gap-1 font-medium hover:underline',
                    payslip.status === 'cancelled' ? 'line-through' : 'text-brand-700',
                  )}
                >
                  <FileText size={13} />
                  {payslip.number}
                </a>
              </td>
              <td className="whitespace-nowrap">
                {formatDate(payslip.period_from)} – {formatDate(payslip.period_to)}
              </td>
              <td className="text-right tabular-nums">{minutesToHours(payslip.minutes)}</td>
              <td className="text-right font-semibold tabular-nums">{formatMoney(payslip.total)}</td>
              <td>
                <StatusBadge payslip={payslip} />
              </td>
              <td className="whitespace-nowrap text-right text-xs">
                {!readOnly && payslip.status === 'issued' ? (
                  <button
                    type="button"
                    className="mr-3 text-action-700 hover:underline"
                    disabled={paid.isPending}
                    onClick={() => {
                      if (window.confirm(t('payroll.paidConfirm', { number: payslip.number, total: formatMoney(payslip.total) })))
                        paid.mutate(payslip.id)
                    }}
                  >
                    {t('payroll.markPaid')}
                  </button>
                ) : null}
                {!readOnly && payslip.status !== 'cancelled' ? (
                  <button
                    type="button"
                    className="text-slate-500 hover:text-red-600 hover:underline"
                    disabled={cancel.isPending}
                    onClick={() => {
                      const question =
                        payslip.status === 'paid'
                          ? t('payroll.cancelPaidConfirm', { number: payslip.number })
                          : t('payroll.cancelConfirm', { number: payslip.number })
                      if (window.confirm(question)) cancel.mutate(payslip.id)
                    }}
                  >
                    {t('payroll.cancel')}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function StatusBadge({ payslip }: { payslip: Payslip }) {
  const { t } = useTranslation()
  const styles = {
    issued: 'bg-amber-50 text-amber-700',
    paid: 'bg-green-100 text-green-800',
    cancelled: 'bg-slate-100 text-slate-500',
  }
  return (
    <span className={cn('rounded px-1.5 py-0.5 text-[11px] font-medium', styles[payslip.status])}>
      {t(`payroll.statuses.${payslip.status}`)}
      {payslip.status === 'paid' && payslip.paid_at ? ` ${formatDate(payslip.paid_at)}` : ''}
    </span>
  )
}

/**
 * „Vytvoriť výplatný list“ za zvolené obdobie - zo všetkých odmien na výplatu,
 * alebo len z riadkov vybraných v tabuľke.
 */
export function CreatePayslipDialog({
  instructorId,
  periodFrom,
  periodTo,
  taughtOnly,
  centerIds = [],
  toPay,
  toPayCount,
  selectedIds,
  selectedTotal,
  onClose,
}: {
  instructorId: number
  periodFrom: string
  periodTo: string
  taughtOnly: boolean
  /** Filter stredísk na stránke - „všetky na výplatu“ len z nich. */
  centerIds?: number[]
  toPay: number
  toPayCount: number
  selectedIds: number[]
  selectedTotal: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [mode, setMode] = useState<'all' | 'selected'>(selectedIds.length ? 'selected' : 'all')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      payrollApi.createPayslip({
        instructor_id: instructorId,
        period_from: periodFrom,
        period_to: periodTo,
        taught_only: taughtOnly,
        center_ids: centerIds,
        ...(mode === 'selected' ? { reward_ids: selectedIds } : {}),
        note,
      }),
    onSuccess: (payslip) => {
      for (const key of ['payslips', 'instructor-rewards', 'rewards-overview']) {
        void qc.invalidateQueries({ queryKey: [key] })
      }
      window.open(payrollApi.pdfUrl(payslip.id), '_blank', 'noopener')
      onClose()
    },
    onError: (err) =>
      setError(
        apiErrorCode(err) === 'payslip.invalidRewards'
          ? t('payroll.invalidRewards')
          : apiErrorCode(err) === 'payslip.empty'
            ? t('payroll.nothingToPay')
            : apiErrorMessage(err),
      ),
  })

  const count = mode === 'all' ? toPayCount : selectedIds.length
  const total = mode === 'all' ? toPay : selectedTotal

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={t('payroll.createTitle')}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('global.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!count || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? <Spinner /> : null}
            {t('payroll.create')}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        {error ? <Alert variant="error">{error}</Alert> : null}
        <p className="text-slate-600">
          {t('payroll.period')}: <strong>{formatDate(periodFrom)} – {formatDate(periodTo)}</strong>
          {taughtOnly ? <span className="ml-1 text-xs text-slate-500">({t('units.countTaught')})</span> : null}
        </p>
        <label className="flex items-start gap-2">
          <input type="radio" className="mt-0.5" checked={mode === 'all'} onChange={() => setMode('all')} />
          <span>
            {t('payroll.allToPay')}
            <span className="ml-1 text-slate-500">({t('payroll.rows', { count: toPayCount })} · {formatMoney(toPay)})</span>
          </span>
        </label>
        <label className={cn('flex items-start gap-2', !selectedIds.length && 'opacity-50')}>
          <input
            type="radio"
            className="mt-0.5"
            disabled={!selectedIds.length}
            checked={mode === 'selected'}
            onChange={() => setMode('selected')}
          />
          <span>
            {t('payroll.onlySelected')}
            <span className="ml-1 text-slate-500">
              ({t('payroll.rows', { count: selectedIds.length })} · {formatMoney(selectedTotal)})
            </span>
          </span>
        </label>
        <label className="block">
          <span className="label">{t('payroll.note')}</span>
          <Textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="flex justify-between font-semibold">
            <span>{t('payroll.totalToPay')}</span>
            <span className="tabular-nums">{formatMoney(total)}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">{t('payroll.lockHint')}</p>
        </div>
      </div>
    </Modal>
  )
}
